/*
 * File: persistent.ts
 * File Created: 2024-11-07 07:15:49
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-11-11 09:03:52
 * Modified By: Val Liu
 * -----
 */

/**
 * Decorator to make a property persistent using localStorage.
 */
const DEBUG = false;
export function persistent(keyFunction?: (vm: Vue) => string | null) {
    return function (target: any, propertyKey: string) {
        keyFunction =
            keyFunction ??
            ((vm: Vue) => {
                const routePath = vm.$route?.path || "";
                const className = vm.constructor.name || "";
                const propName = (vm as any).$attrs?.name; // Accessing the 'name' prop
                if (propName == null) {
                    DEBUG &&
                        console.log(
                            "propName not exists.",
                            vm,
                            routePath,
                            className,
                            propName,
                        );
                    return null;
                }
                return `${routePath}-${propName}-${className}-${propertyKey}`;
            });

        // Store the original created method, if any
        const originalCreated = target.created;

        // Define the new created method
        target.created = function () {
            // Call the original created method
            if (originalCreated) {
                originalCreated.call(this);
            }

            const vm = this;

            // Function to initialize the property from localStorage
            const initializeProperty = () => {
                const localKey = keyFunction!(vm);
                // invalid key
                if (localKey == null) return;
                const storedValue = localStorage.getItem(localKey);

                DEBUG && console.log("initializeProperty", localKey, storedValue);
                if (storedValue !== null) {
                    try {
                        vm[propertyKey] = JSON.parse(storedValue);
                    } catch (e) {
                        console.error(
                            `Failed to parse stored value for ${propertyKey}:`,
                            e,
                        );
                    }
                }
            };

            // Initialize the property
            initializeProperty();

            // Add a watcher for the property using this.$watch
            this.$watch(
                propertyKey,
                (newVal: any) => {
                    const localKey = keyFunction!(this);
                    // invalid key
                    if (localKey == null) return;
                    DEBUG && console.log("property changed", localKey, newVal);
                    localStorage.setItem(localKey, JSON.stringify(newVal));
                },
                { deep: true },
            );

            // Watch for changes to the 'name' prop and re-initialize the property
            this.$watch("name", () => {
                DEBUG && console.log("name prop changed, re-initializing property");
                initializeProperty();
            });
        };
    };
}
